// Upload panel: equipment wizard + PDF picker + progress + cancel.
import { useRef, useState } from 'react';
import EquipmentWizard from './EquipmentWizard';
import { useManualUpload, MAX_PDF_BYTES } from './useManualUpload';
import { PAYMENTS_VISIBLE } from '../brand';

function ProgressRow({ label, pct }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'var(--mono)',
          fontSize: 10.5,
          color: 'var(--text-3)',
          letterSpacing: 1,
          marginBottom: 6,
        }}
      >
        <span>{label}</span>
        <span>{pct}%</span>
      </div>
      <div className="progress-track" style={{ marginBottom: 0 }}>
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function UploadManual({ plan, manualCount, onUploaded }) {
  const upload = useManualUpload();
  const [equipmentPath, setEquipmentPath] = useState(null);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const atLimit = plan && manualCount >= plan.max_manuals;
  const busy = ['extracting', 'uploading', 'finalizing'].includes(upload.phase);

  function pickFile(e) {
    const picked = e.target.files?.[0] || null;
    setFile(picked);
    upload.reset();
  }

  async function startUpload() {
    await upload.start(file, equipmentPath);
  }

  // Refresh the list exactly once when an upload completes.
  const notifiedRef = useRef(null);
  if (upload.phase === 'done' && notifiedRef.current !== upload) {
    notifiedRef.current = upload;
    setTimeout(() => onUploaded?.(), 0);
  }

  return (
    <div className="panel">
      <div className="panel-label">
        <span className="panel-tick" style={{ background: 'var(--cyan)' }} /> UPLOAD A MANUAL
      </div>

      {atLimit && (
        <div
          style={{
            background: 'rgba(251,191,36,0.1)',
            border: '1px solid rgba(251,191,36,0.3)',
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--yellow)',
            marginBottom: 4,
          }}
        >
          Your {plan.plan} plan allows {plan.max_manuals} manual
          {plan.max_manuals === 1 ? '' : 's'} and you have {manualCount}. Delete a manual below to
          free a slot{PAYMENTS_VISIBLE ? ', or upgrade your plan' : ''}.
        </div>
      )}

      {!atLimit && (
        <>
          <EquipmentWizard onChange={path => setEquipmentPath(path)} />

          <div style={{ marginTop: 16 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={pickFile}
              disabled={busy}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                className="btn btn-ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
              >
                {file ? 'Choose another PDF' : 'Choose PDF'}
              </button>
              {file && (
                <span style={{ fontSize: 12.5, color: 'var(--text-2)', wordBreak: 'break-all' }}>
                  {file.name} · {(file.size / (1024 * 1024)).toFixed(1)}MB
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 8 }}>
              One PDF at a time, up to {Math.round(MAX_PDF_BYTES / (1024 * 1024))}MB. Text PDFs only
              — scanned documents are not supported yet.
            </div>
          </div>

          {!busy && upload.phase !== 'done' && (
            <button
              className="btn"
              onClick={startUpload}
              disabled={!file || !equipmentPath}
              style={{
                marginTop: 16,
                background: 'var(--primary)',
                color: '#ffffff',
              }}
            >
              Upload manual →
            </button>
          )}

          {busy && (
            <div style={{ marginTop: 18 }}>
              <ProgressRow label="1. READING PDF" pct={upload.extractPct} />
              <ProgressRow label="2. INDEXING (EMBEDDINGS)" pct={upload.uploadPct} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                  {upload.phase === 'extracting' && 'Extracting text in your browser…'}
                  {upload.phase === 'uploading' && 'Uploading and indexing content…'}
                  {upload.phase === 'finalizing' && 'Finishing up…'}
                </span>
                <button
                  className="btn btn-ghost"
                  onClick={upload.cancel}
                  style={{ marginLeft: 'auto', padding: '7px 14px', fontSize: 11 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {upload.phase === 'done' && (
            <div
              style={{
                marginTop: 16,
                background: 'rgba(52,211,153,0.1)',
                border: '1px solid rgba(52,211,153,0.3)',
                borderRadius: 10,
                padding: '12px 16px',
                fontSize: 13,
                color: 'var(--green)',
              }}
            >
              ✓ Manual uploaded and indexed. It is now used to answer your questions for this
              equipment.
            </div>
          )}

          {upload.phase === 'cancelled' && (
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-2)' }}>
              Upload cancelled.
            </div>
          )}

          {upload.phase === 'error' && (
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--red)' }}>{upload.error}</div>
          )}
        </>
      )}

      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 14, lineHeight: 1.55 }}>
        Upload only documents you have the right to use. Your documents stay private to your account
        and are never shared with other users.
      </div>
    </div>
  );
}
